import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import Table from '../../../../components/Table';
import CollegeCornerFilters from '../../filters/CollegeCornerFilters';

import { initialState } from '../helpers';
import { columns, customText, initialCustomOptions, initialFilters } from './helpers';
import { queryCollegeRankingsAPI, queryCollegeRankingsSnapshotAPI } from '../../../../actions/college-corner.actions';
import { setQueryParams } from '../../../../utils/collegeCorner';

import styles from './CollegeCornerCollegeRankings.scss';

const CollegeCornerCollegeRankings = ({ history, location, isPowerUser, page, hideMaskedP6 }) => {
    const dispatch = useDispatch();
    const [data, setData] = useState(initialState);
    const [filters, setFilters] = useState(initialFilters);
    const [tempFilters, setTempFilters] = useState({});
    const [customOptions, setCustomOptions] = useState(initialCustomOptions);

    const callCollegeRankingSnapshotAPI = async () => dispatch(queryCollegeRankingsSnapshotAPI());

    const callCollegeRankingsAPI = async (params) => {
        try {
        const response = await Promise.resolve(dispatch(queryCollegeRankingsAPI(params)));
        const count = response.length;
        if (response) {
            setData({
            rows: response,
            count,
            isLoad: false,
            });
        }
        } catch (error) {
        console.log(error);
        }
    };

    const callCollegeRankingsSnapshotAPI = async () => {
        try {
        const response = await callCollegeRankingSnapshotAPI();
        if (response) {
            const extractedDivisions = response.divisions.map((division, idx) => ({
            label: division.shortName,
            value: division.divisionName,
            description: division.divisionName,
            id: idx + 1,
            }));

            const extractedDates = response.snapshotDates.map((date, idx) => ({
            label: date.snapshotDateLabel,
            value: date.snapshotDateLabel,
            description: date.snapshotDateLabel,
            id: idx + 1,
            }));

            setCustomOptions({ ...customOptions, division: extractedDivisions, snapshotDates: extractedDates });
        }
        } catch (error) {
        console.log(error);
        }
    };

    useEffect(() => {
        callCollegeRankingsSnapshotAPI();
    }, []);

    useEffect(() => {
        setFilters({
        ...filters,
        division: defaultDivisionName(),
        snapshotDate: defaultSnapshotDates(),
        });
    }, [customOptions]);

    useEffect(() => {
        if (filters) {
        const query = setQueryParams(filters);
        history.replace(`${location.pathname}?${query}`);
        }
    }, [filters]);

    const handleSort = (sortValue) => {
        const sorting = sortValue === filters.sort ? sortValue : filters.sort;
        const order = filters.order === 'asc' ? 'desc' : 'asc';

        setFilters(prevFilters => ({
        ...prevFilters,
        sort: sorting,
        order: sortValue === prevFilters.sort ? order : 'desc',
        page: 1,
        }));
    };

    const handleLoadMore = () => {
        setFilters(prevFilters => ({
        ...prevFilters,
        page: prevFilters.page + 1,
        }));
    };

    const handleUpdateFilters = (updatedFilters) => {
        setTempFilters(prevFilters => ({
        ...prevFilters,
        ...updatedFilters,
        }));
    };

    const handleFilterSubmit = (props = {}) => {
        setFilters(prevFilters => ({
        ...prevFilters,
        ...tempFilters,
        ...props,
        page: 1,
        }));
        setTempFilters({});
    };

    const handleResetFilters = () => {
        setTempFilters({});
        setFilters({
        ...initialFilters,
        sort: isPowerUser ? initialFilters.sort : 'rank',
        division: defaultDivisionName(),
        snapshotDate: defaultSnapshotDates(),
        });
    };

    const findValueAtIndex = (arr, index) => (index >= 0 && index < arr.length ? arr[index] : null);

    const defaultDivisionName = () => {
        return findValueAtIndex(customOptions.division, 0)?.value;
    };

    const defaultSnapshotDates = () => {
        return findValueAtIndex(customOptions.snapshotDates, customOptions.snapshotDates.length - 1)?.value;
    };

    const hidePlayerP6Ratings = hideMaskedP6 ? ['power6masked'] : ['power6'];

    return (
        <div className={`${styles.CollegeRankings} ${!isPowerUser ? styles.withNoPowerUser : ''}`}>
            <CollegeCornerFilters
                tempFilters={tempFilters}
                defaultFilters={filters}
                onhandleUpdateFilters={handleUpdateFilters}
                onhandleFilterSubmit={handleFilterSubmit}
                onhandleResetFilters={handleResetFilters}
                filterOptions={['gender', 'division', 'snapshotDate']}
                sortingOptions={columns.map(({ title, key, sortable }) => ({
                label: title,
                value: key,
                sortable: sortable !== false && !(title === 'P6' && !isPowerUser),
                })).filter(Boolean)}
                customOptions={customOptions}
                page={page}
            />
            <div className={`${styles.dividerWhite}`} />
            <Table
                data-testid="college-corner.college-rankings.table"
                sortRuleChanger={handleSort}
                columns={columns.map(({ title, custom, key, maxWidth, rowClassName }) => ({
                title,
                custom,
                key,
                maxWidth,
                rowClassName,
                hideColumn: hidePlayerP6Ratings.includes(key),
                }))}
                data={data}
                filters={filters}
                asyncCall={callCollegeRankingsAPI}
                onhandleClickLoadMore={handleLoadMore}
                customNoRowsText={customText}
                mergeData
            />
        </div>
    );
};

CollegeCornerCollegeRankings.propTypes = {
    history: PropTypes.instanceOf(Object).isRequired,
    location: PropTypes.instanceOf(Object).isRequired,
    isPowerUser: PropTypes.bool,
    hideMaskedP6: PropTypes.bool,
    page: PropTypes.string,
};

CollegeCornerCollegeRankings.defaultProps = {
    isPowerUser: false,
    hideMaskedP6: false,
    page: null,
};

export default CollegeCornerCollegeRankings;
